import { memo } from "react";
import Color from "@tiptap/extension-color";
import Emoji from "@tiptap/extension-emoji";
import Heading from "@tiptap/extension-heading";
import Image from "@tiptap/extension-image";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Youtube from "@tiptap/extension-youtube";
import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import { ReactComponent as ArrowRight } from "../../Assets/angle-right-solid.svg";
import { ReactComponent as DoneIcon } from "../../Assets/circle-check-solid.svg";
import { ReactComponent as OpenIcon } from "../../Assets/inbox-solid.svg";
import { ReactComponent as ProgressIcon } from "../../Assets/loading.svg";
import { activateOnKey } from "../../Helper/a11y";
import { Callout } from "../../Helper/Callout";
import { contentWithVariables } from "../../Helper/ContentReplace";
import CustomLink from "../../Helper/CustomLink";
import {
  getDataDescription,
  stripHtml,
  truncate,
} from "../../Helper/FeedbackItemHelper";
import IFrame from "../../Helper/IFrame";
import { flattenLegacyArtifacts } from "../../Helper/LegacyArtifacts";
import { removeMarkdown } from "../../Helper/RemoveMarkdown";
import { isOnline } from "../../Helper/OnlineState";
import { replaceAll } from "../../Helper/String";
import { ServerVariableNode } from "../../Helper/VariableExtension";
import { useConfigStore } from "../../Stores/Config";
import { useRouterStore } from "../../Stores/Router";
import { useSessionStore } from "../../Stores/Session";
import { getLastUpdateDate, Ticket } from "../../Stores/Ticket";
import { HelpcenterLinkExtension } from "../tiptap/extenstions/custom/HelpCenterLink/HelpCenterLinkExtension";
import { AvatarImage } from "../AvatarImage/AvatarImage";
import { getOperatorInfo } from "../WidgetHeader/WidgetHeader";
import "./ConversationItem.scss";

// tiptap schema/extension configs are static — build them once at module
// scope. Constructing this array per row per render was a measurable part of
// the tab-switch cost (the Conversations view remounts on every navigation).
const PREVIEW_EXTENSIONS = [
  // link: false — CustomLink below provides the 'link' extension; StarterKit
  // v3 ships its own and registering both trips tiptap's duplicate-name warn.
  StarterKit.configure({ codeBlock: {}, heading: false, link: false }),
  Heading.configure({
    levels: [1, 2, 3],
  }),
  Callout,
  Mention,
  Placeholder,
  CustomLink.configure({
    protocols: [],
    validate: (url: string) => {
      return /^[a-zA-Z][a-zA-Z0-9+.-]*:|^\/|^#|^\?/.test(url);
    },
  }),
  Image,
  TextStyle,
  ServerVariableNode,
  Youtube.configure({ controls: true }),
  IFrame,
  HelpcenterLinkExtension,
  Color.configure({
    types: ["textStyle"],
  }),
  Emoji,
];

// generateHTML (tiptap doc parse) is the expensive part of a list row. The
// routed page REMOUNTS on every tab switch (App keys the content container),
// so per-instance memoization (useMemo/React.memo) would recompute anyway —
// cache by the tiptap content object itself, which is replaced wholesale when
// fresh data lands, so entries invalidate (and GC) naturally.
const previewTextCache = new WeakMap<object, string>();

const previewTextFromTiptapContent = (contentData: object): string | null => {
  const cached = previewTextCache.get(contentData);
  if (cached !== undefined) {
    return cached;
  }

  try {
    var html = generateHTML(contentData as any, PREVIEW_EXTENSIONS);

    html = replaceAll(html, "\n", "<br>");
    html = replaceAll(html, "><", "> <");

    const text = stripHtml(html);
    previewTextCache.set(contentData, text);
    return text;
  } catch (exp) {
    return null;
  }
};

function ConversationItem({
  ticket,
  index,
}: {
  ticket: Ticket;
  index: number;
}) {
  const routerStore = useRouterStore();
  const config = useConfigStore();

  const openingTimeAutoResponderText = () => {
    // Use the ticket's server-resolved team-effective config when present, so
    // the conversation list preview matches the in-conversation rendering.
    const effectiveHoursConfig = ticket.effectiveHoursConfig;
    const { online, nextOpeningTime, currentOpeningTime } =
      isOnline(effectiveHoursConfig);

    var message = "";
    if (online) {
      // Per-row override → team-level replyTime (already localized server-side)
      // → project-level key resolved via translateText. If all empty, render
      // nothing (avoids bare "." subtitle when no replyTime is configured).
      const text =
        currentOpeningTime?.replyTime ??
        effectiveHoursConfig?.replyTime ??
        config.translateText(config.config?.replyTime);
      message = text && text.trim() !== "" ? `${text}.` : "";
    } else {
      const teamOoo =
        effectiveHoursConfig?.staticTranslations?.outOfOfficeMessage;
      if (teamOoo && typeof teamOoo === "string" && teamOoo.trim() !== "") {
        message = teamOoo;
      } else {
        const oooMessage = config.translateText("outOfOfficeMessage");
        if (oooMessage.trim() !== "" && oooMessage !== "outOfOfficeMessage") {
          message = config.translateText("outOfOfficeMessage");
        } else {
          message = `${config.translateText(
            "weAreOffline",
          )} ${config.translateText("weAreBack")} <b>🕘 ${config.formatRelativeTime(nextOpeningTime)}</b>.`;
        }
      }
    }

    return message;
  };

  const getTextFromLatestComment = () => {
    var text = null;
    if (
      ticket.latestPublicComment?.data &&
      ticket.latestPublicComment?.data.text
    ) {
      text = ticket.latestPublicComment?.data.text;
    }

    if (!text && ticket.latestPublicComment?.text) {
      text = ticket.latestPublicComment.text;
    }

    var contentData = ticket.latestPublicComment?.data?.content;

    if (
      contentData &&
      (typeof contentData === "string" || contentData instanceof String)
    ) {
      // Legacy Styled-reply <artifact> blocks read as their text, never as
      // raw HTML, in the conversation-list preview snippet.
      text = flattenLegacyArtifacts(String(contentData));
    }

    if (contentData && typeof contentData === "object") {
      const previewText = previewTextFromTiptapContent(contentData);
      if (previewText !== null) {
        text = previewText;
      }
    }

    if (ticket.form?.description?.value) {
      text = ticket.form?.description?.value;
    }

    const trimmedText = typeof text === "string" ? text.trim() : "";
    const isLegacyAttachmentLiteral = trimmedText === "📎 Attachment";

    const attachments = ticket.latestPublicComment?.attachments;
    const hasImageNode = (node: any): boolean => {
      if (!node || typeof node !== "object") return false;
      if (node.type === "image") return true;
      if (Array.isArray(node.content)) {
        return node.content.some(hasImageNode);
      }
      return false;
    };

    if (trimmedText && !isLegacyAttachmentLiteral) {
      return contentWithVariables(text, {
        name: useSessionStore.getState().getName(),
      });
    }

    const firstAttachment =
      attachments && attachments.length > 0 ? attachments[0] : null;
    const mime: string = firstAttachment?.type ?? "";
    const name: string = firstAttachment?.name ?? "";
    const tiptapContent = ticket.latestPublicComment?.data?.content;
    const inlineImage =
      typeof tiptapContent === "object" && hasImageNode(tiptapContent);

    const isImage =
      mime.startsWith("image/") ||
      /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/i.test(name) ||
      inlineImage;
    const isVideo =
      mime.startsWith("video/") || /\.(mp4|mov|webm|mkv|avi)$/i.test(name);
    const isAudio =
      mime.startsWith("audio/") || /\.(mp3|wav|ogg|m4a|aac)$/i.test(name);

    if (firstAttachment || inlineImage || isLegacyAttachmentLiteral) {
      let emoji = "📎";
      let key = "sentAttachment";
      let fallback = "Attachment";
      if (isImage) {
        emoji = "📷";
        key = "sentPhoto";
        fallback = "Photo";
      } else if (isVideo) {
        emoji = "🎬";
        key = "sentVideo";
        fallback = "Video";
      } else if (isAudio) {
        emoji = "🎤";
        key = "sentAudio";
        fallback = "Audio message";
      }
      const translated = config.translateText(key);
      const label = translated === key ? fallback : translated;
      return `${emoji} ${label}`;
    }

    if (ticket.latestPublicComment?.data?.showReplyTimes) {
      var openingTime = openingTimeAutoResponderText();
      if (openingTime) {
        return openingTime;
      }
    }

    return null;
  };

  const getDescription = () => {
    var description = getDataDescription({ data: ticket, maxLength: 100 });

    if (ticket.latestPublicComment) {
      const textFromLatestComment = getTextFromLatestComment();
      if (textFromLatestComment) {
        description = textFromLatestComment;
      }
    }

    if (description === "No content") {
      description = config.translateText("noMessages");
    }

    return truncate(removeMarkdown(description), 58);
  };

  const operatorInfo = getOperatorInfo(config, config.config, ticket);

  const renderAvatarImage = () => {
    if (operatorInfo?.image) {
      return (
        <div
          className={`operator-info-image ${
            operatorInfo?.bot ? "operator-info-image--bot" : ""
          }`}
        >
          {operatorInfo?.image}
        </div>
      );
    }

    if (config.team && config.team.length > 0) {
      const team = config.team.slice(0, 3);
      if (team.length > 2) {
        return (
          <div className="operator-info-team-image">
            {team.map((teamUser, index) => {
              return (
                <AvatarImage
                  key={index}
                  src={teamUser.profileImageUrl}
                  alt={teamUser.firstName}
                />
              );
            })}
          </div>
        );
      } else {
        return (
          <div className="operator-info-image">
            <AvatarImage
              src={team[0].profileImageUrl}
              alt={team[0].firstName}
            />
          </div>
        );
      }
    }

    return null;
  };

  // Check if type conversation with no comments yet.
  /*if (
    (ticket.type === "INQUIRY" || ticket.type === "BOT") &&
    !ticket.latestPublicComment
  ) {
    return null;
  }*/

  const renderTitle = () => {
    const stt = config.config?.stt ?? true;
    const stn = config.config?.stn ?? false;
    const sts = config.config?.sts ?? false;

    if (!stn && !stt && !sts) {
      return null;
    }

    if (!ticket.title && !ticket.bugId) {
      return null;
    }

    let title = "";

    if (stn && ticket.bugId) {
      title += `#${ticket.bugId}`;
    }

    if (stt && ticket.title) {
      title += title.length > 0 ? ` • ${ticket.title}` : ticket.title;
    }

    let StatusIcon: any = null;
    if (sts && ticket?.status) {
      const currentStatus = ticket.status;

      StatusIcon = OpenIcon;

      if (currentStatus !== "OPEN") {
        StatusIcon = ProgressIcon;
      }

      if (currentStatus === "DONE") {
        StatusIcon = DoneIcon;
      }
    }

    if (title.length === 0) {
      return null;
    }

    return (
      <div className="conversation-item-main-title">
        {StatusIcon ? <StatusIcon aria-label={ticket.status} /> : null}
        <span>{title}</span>
      </div>
    );
  };

  return (
    <div
      className={`conversation-item ${
        ticket.sessionNotificationsUnread && "conversation-item--unread"
      }`}
      role="button"
      tabIndex={0}
      aria-label={getDescription()}
      onClick={() => {
        routerStore.pushPage("conversation", {
          shareToken: ticket.shareToken,
        });
      }}
      onKeyDown={activateOnKey(() => {
        routerStore.pushPage("conversation", {
          shareToken: ticket.shareToken,
        });
      })}
    >
      <div className="conversation-item--inner">
        <div className="conversation-item-avatar">{renderAvatarImage()}</div>
        <div className="conversation-item-labels">
          {renderTitle()}
          <div className="conversation-item-title">{getDescription()}</div>
          <div className="conversation-item-description">
            {operatorInfo?.name && `${operatorInfo.name} • `}
            {getLastUpdateDate(ticket).fromNow()}
          </div>
        </div>
        {ticket.sessionNotificationsUnread ? (
          <div className="conversation-item-unreaddot" aria-hidden="true" />
        ) : (
          <ArrowRight className="conversation-item-indicator" />
        )}
      </div>
    </div>
  );
}

// memo: rows derive their preview from stable store objects; App re-renders
// on every navigation and must not cascade a re-render of each (tiptap-heavy)
// row of an inbox that didn't change.
export default memo(ConversationItem);
